'use client';

import type { RelatedLink } from '../../entities';
import { relatedLinkTag } from '../../related-links-data';

export interface RelatedLinksProps {
  heading: string;
  description?: string;
  links: RelatedLink[];
  entityLabel?: string;
  onLinkClick?: (link: RelatedLink) => void;
}

export const RelatedLinks = ({ heading, description, links, entityLabel, onLinkClick }: RelatedLinksProps) => (
  <div className="flex flex-col">
    <div className="mb-1.5 ml-2.5 mr-2.5 mt-0 flex items-center gap-2 border-b border-[var(--hubnav-line)] py-1">
      <h4 className="m-0 text-[11px] font-bold uppercase leading-tight tracking-[0.06em] text-[var(--hubnav-muted)]">
        {heading}
      </h4>
      {entityLabel ? (
        <span className="rounded bg-[color-mix(in_srgb,var(--hubnav-accent)_12%,transparent)] px-1.5 py-0.5 text-[11px] font-bold tabular-nums tracking-[0.02em] text-[var(--hubnav-accent)]">
          {entityLabel}
        </span>
      ) : null}
    </div>
    {description ? (
      <p className="mb-2 mt-0 px-2.5 text-[13px] leading-snug text-[var(--hubnav-muted)]">{description}</p>
    ) : null}
    {entityLabel ? <p className="sr-only">Linked to {entityLabel}</p> : null}
    <ul className="mb-0 mt-1 flex list-none flex-col gap-0.5 p-0 max-[1080px]:grid max-[1080px]:grid-cols-2">
      {links.map(link => (
        <li key={`${link.href}-${link.label}`}>
          <a
            className="group flex min-h-9 items-center rounded-md px-2.5 py-1.5 no-underline transition-colors duration-100 ease-out hover:bg-[var(--hubnav-panel-bg)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--hubnav-accent)] max-[1080px]:min-h-11"
            href={link.href}
            onClick={() => onLinkClick?.(link)}
          >
            <span className="flex min-w-0 flex-col">
              <span className="text-sm font-semibold leading-tight text-[var(--hubnav-fg)] transition-colors duration-100 ease-out group-hover:text-[var(--hubnav-hover-text)]">
                {link.label}
              </span>
              <span className="mt-px text-xs text-[var(--hubnav-muted)]">{relatedLinkTag(link)}</span>
            </span>
          </a>
        </li>
      ))}
    </ul>
  </div>
);
